import { useState } from 'react';
import { getScoreColor } from '../utils/scoring';
import TickSlider from './TickSlider';

const TOUCHPOINTS = [
  { id: 'websiteSpeed', label: 'Website Load Speed', desc: 'Does the site load in under 3 seconds?', category: 'Website' },
  { id: 'mobileResponsive', label: 'Mobile Responsiveness', desc: 'Does the site work perfectly on phone?', category: 'Website' },
  { id: 'menuAccessible', label: 'Menu Accessibility', desc: 'Can you find the menu within 2 taps/clicks?', category: 'Website' },
  { id: 'hoursVisible', label: 'Hours & Location Visible', desc: 'Are hours, address, phone easy to find?', category: 'Website' },
  { id: 'photoQuality', label: 'Photo Quality', desc: 'Professional food/space photography?', category: 'Website' },
  { id: 'reservationFlow', label: 'Reservation Flow', desc: 'How many clicks to book? Is it frictionless?', category: 'Booking' },
  { id: 'resyPresence', label: 'Resy/OpenTable Presence', desc: 'Listed on reservation platforms with accurate info?', category: 'Booking' },
  { id: 'confirmationFlow', label: 'Booking Confirmation', desc: 'Clear confirmation, reminder, and modification process?', category: 'Booking' },
  { id: 'googleProfile', label: 'Google Business Profile', desc: 'Complete, accurate, photos, responded to reviews?', category: 'Search' },
  { id: 'seoVisibility', label: 'SEO Visibility', desc: 'Do they appear in relevant searches?', category: 'Search' },
  { id: 'reviewResponse', label: 'Review Response Rate', desc: 'Do they respond to Google/Yelp reviews?', category: 'Search' },
  { id: 'instaLink', label: 'Social Links Present', desc: 'Instagram, Facebook linked from website?', category: 'Social' },
  { id: 'contentFreshness', label: 'Content Freshness', desc: 'Is recent content being posted? Last post < 7 days?', category: 'Social' },
  { id: 'orderOnline', label: 'Online Ordering', desc: 'Can guests order pickup/delivery easily?', category: 'Ordering' },
  { id: 'giftCards', label: 'Gift Cards / Merch', desc: 'Can guests buy gift cards or merch online?', category: 'Revenue' },
  { id: 'emailCapture', label: 'Email Capture', desc: 'Is there a way to capture guest emails?', category: 'Revenue' },
];

export default function DigitalFriction({ audit, onUpdate }) {
  const df = audit.digitalFriction || {};

  const updateScore = (id, value) => {
    onUpdate({ ...audit, digitalFriction: { ...df, [id]: { ...df[id], score: value } } });
  };

  const updateNotes = (id, value) => {
    onUpdate({ ...audit, digitalFriction: { ...df, [id]: { ...df[id], notes: value } } });
  };

  const scored = TOUCHPOINTS.filter(t => (df[t.id]?.score || 0) > 0);
  const avgScore = scored.length > 0 ? scored.reduce((s, t) => s + df[t.id].score, 0) / scored.length : 0;
  const categories = [...new Set(TOUCHPOINTS.map(t => t.category))];

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg text-white">Digital Friction Score</h3>
          <p className="text-xs text-neutral-500">Audit every digital touchpoint — website, booking, search, social</p>
        </div>
        {avgScore > 0 && (
          <div className="text-right">
            <p className={`text-2xl font-black ${getScoreColor(avgScore).text}`}>{avgScore.toFixed(1)}</p>
            <p className="text-[10px] text-neutral-500">{scored.length}/{TOUCHPOINTS.length}</p>
          </div>
        )}
      </div>

      {categories.map(cat => (
        <div key={cat} className="mb-4">
          <p className="text-[10px] text-brand-gold font-semibold mb-2 uppercase tracking-wider">{cat}</p>
          <div className="space-y-2">
            {TOUCHPOINTS.filter(t => t.category === cat).map(tp => {
              const score = df[tp.id]?.score || 0;
              const color = getScoreColor(score);
              return (
                <div key={tp.id} className="bg-neutral-800/20 rounded-lg p-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <span className="text-xs text-neutral-300">{tp.label}</span>
                      <span className="text-[9px] text-neutral-600 ml-2">{tp.desc}</span>
                    </div>
                    {score > 0 && <span className={`text-xs font-bold ${color.text}`}>{score.toFixed(1)}</span>}
                  </div>
                  <TickSlider value={score} onChange={v => updateScore(tp.id, v)} showValue={false} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
